import React, { useContext, useEffect } from 'react'
import { Modal, Form, Input, Select, Button, Space } from 'antd'
import { useQuery } from '@apollo/react-hooks'
import { GET_USERS_FOR_SELECT } from 'modules/Users/graphql/Queries'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserSave } from 'utils/User'

const { Option } = Select

const DepartmentModal = ({
  visible,
  onCancel,
  onOk,
  editingDepartment,
  departments,
}) => {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const hasEdit = canUserEdit(userPermissions || currentUser, isClient)
  const hasAdd = canUserAdd(userPermissions || currentUser, isClient)
  const hasSave = canUserSave(userPermissions || currentUser, isClient)
  const canSubmit = editingDepartment ? (hasEdit && hasSave) : (hasAdd && hasSave)

  const [form] = Form.useForm()

  const { data, loading } = useQuery(GET_USERS_FOR_SELECT, {
    variables: { where: { isActive: "true",role: ['STAFF', 'SALES_EXECUTIVE', 'ADMIN'], }, skip: 0, limit: 100000 },
  })
  const users = data?.getUsers?.users || []
  let initialValues = editingDepartment
    ? { ...editingDepartment, departmentHead: editingDepartment.head?.id }
    : {}

  useEffect(() => {
    if (editingDepartment) {
      form.setFieldsValue(initialValues)
    } else {
      form.resetFields()
    }
  }, [editingDepartment, form, initialValues])

  const handleSubmit = () => {
    if (!canSubmit) return
    form.validateFields().then((values) => {
      onOk(values)
    })
  }

  return (
    <Modal
      title={editingDepartment ? 'Edit Department' : 'Add Department'}
      visible={visible}
      forceRender
      onCancel={() => {
        form.resetFields()
        onCancel()
      }}
      footer={null}
    >
      <Form
        form={form}
        layout='vertical'
        initialValues={initialValues ? initialValues : {}}
      >
        <Form.Item
          label='Department Name'
          name='departmentName'
          rules={[
            { required: true, message: 'Please enter department name' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value) return Promise.resolve()
                const exists = departments.some(
                  (d) =>
                    d.departmentName.trim().toLowerCase() ===
                    value.trim().toLowerCase(),
                )
                if (!editingDepartment && exists) {
                  return Promise.reject(new Error('Department already exists'))
                }
                return Promise.resolve()
              },
            }),
          ]}
        >
          <Input placeholder='Enter Department Name' />
        </Form.Item>

        <Form.Item
          label='Department Head'
          name='departmentHead'
          rules={[{ required: true, message: 'Please select department head' }]}
        >
          <Select
            showSearch
            loading={loading}
            placeholder='Select Department Head'
            optionFilterProp='children'
            filterOption={(input, option) => {
              const user = users.find((u) => u.id === option.value)
              if (!user) return false
              const searchText =
                `${user.firstName} ${user.lastName} ${user.role}`.toLowerCase()
              return searchText.includes(input.toLowerCase())
            }}
            allowClear
          >
            {users.map((user) => (
              <Option value={user.id} key={user.id}>
                <span
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <span>
                    {user.firstName} {user.lastName}
                  </span>
                  <span
                    style={{
                      marginLeft: 8,
                      color: '#999',
                      fontStyle: 'italic',
                    }}
                  >
                    ({user.role})
                  </span>
                </span>
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item shouldUpdate>
          {() => (
            <Space
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              <Button
                onClick={() => {
                  form.resetFields()
                  onCancel()
                }}
              >
                Cancel
              </Button>
              <Button
                type='primary'
                onClick={handleSubmit}
                disabled={
                  !canSubmit ||
                  !form.isFieldsTouched(true) ||
                  form.getFieldsError().some(({ errors }) => errors.length)
                }
              >
                Submit
              </Button>
            </Space>
          )}
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default DepartmentModal
